import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { chain } from 'lodash';

import { Box } from '@mui/material';
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  YAxis,
  XAxis,
  Area,
  Line,
} from 'recharts';

import { RouterOutput } from '../../api/trpc';
import { fontFamily } from '../../theme/typography';
import { colors } from '../../theme/palette';
import { ChartLegendChip } from '../../atoms/chart-legend-item';
import { Row } from '../../atoms/row';

type Report = RouterOutput['reports']['get'];

type ReportAvgDayChartProps = {
  data: Report['avgDayChart'];
};

export const ReportAvgDayChart = ({ data }: ReportAvgDayChartProps) => {
  const formattedData = useMemo(() => {
    return chain(data)
      .orderBy(['time'], ['asc'])
      .map((d) => {
        return { ...d };
      })
      .value();
  }, [data]);

  return (
    <Box>
      <Box sx={{ width: '100%', height: { xs: 180, sm: 250 } }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={formattedData}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >
            <defs>
              <linearGradient
                id="colorPvProduction"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor={colors.primary}
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor={colors.primary}
                  stopOpacity={0.1}
                />
              </linearGradient>

              <linearGradient id="colorGrid" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={colors.darkBlue}
                  stopOpacity={0.4}
                />
                <stop
                  offset="95%"
                  stopColor={colors.darkBlue}
                  stopOpacity={0.2}
                />
              </linearGradient>

              <linearGradient id="colorConsumption" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={colors.dodgerBlue}
                  stopOpacity={0.4}
                />
                <stop
                  offset="95%"
                  stopColor={colors.dodgerBlue}
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="0"
              stroke="#e0e0e0"
              vertical={false}
            />

            <YAxis
              yAxisId="left"
              axisLine={false}
              tickLine={false}
              width={20}
              tickFormatter={(value) => `${value}`}
              style={{
                fontFamily,
                color: colors.darkBlue,
              }}
            />

            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tickFormatter={(value) => `${value}%`}
              axisLine={false}
              tickLine={false}
              width={40}
              style={{
                fontFamily,
                color: colors.darkBlue,
              }}
            />

            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              ticks={[3, 6, 9, 12, 15, 18, 21]}
              width={40}
            />

            <Area
              yAxisId="left"
              type="monotone"
              dataKey="pvProduction"
              stroke={colors.primary}
              strokeWidth={3}
              fill="url(#colorPvProduction)"
            />

            <Area
              yAxisId="left"
              type="monotone"
              dataKey="grid"
              stroke={colors.darkBlue}
              strokeWidth={3}
              fill="url(#colorGrid)"
            />

            <Area
              yAxisId="left"
              type="monotone"
              dataKey="consumption"
              stroke={colors.dodgerBlue}
              strokeWidth={3}
              fill="url(#colorConsumption)"
            />

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="batterySoc"
              stroke={colors.richBlack}
              strokeWidth={3}
              dot={false}
              fill="none"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      <Row sx={{ gap: 1, mt: 2, flexWrap: 'wrap' }}>
        {[
          {
            label: (
              <FormattedMessage
                id="report.avg_day_chart.pv_production"
                defaultMessage="Výroba FVE"
              />
            ),
            color: colors.primary,
          },
          {
            label: (
              <FormattedMessage
                id="report.avg_day_chart.consumption"
                defaultMessage="Spotřeba"
              />
            ),
            color: colors.dodgerBlue,
          },
          {
            label: (
              <FormattedMessage
                id="report.avg_day_chart.grid"
                defaultMessage="Distribuční síť"
              />
            ),
            color: colors.darkBlue,
          },
          {
            label: (
              <FormattedMessage
                id="report.avg_day_chart.battery_soc"
                defaultMessage="Nabití baterie"
              />
            ),
            color: colors.richBlack,
            colorShape: 'line' as const,
          },
        ].map((item, index) => (
          <Box key={index}>
            <ChartLegendChip {...item} />
          </Box>
        ))}
      </Row>
    </Box>
  );
};
